// src/pages/PublicProfile/utils/UserPostsTab.jsx
import React, { useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { fetchPublicUserPosts } from "../../../../redux/posts/profilePublicPosts/publicUserPostsActions";
import { resetPublicUserPosts } from "../../../../redux/posts/profilePublicPosts/publicUserPostsSlice";

import Post from "../../../Stockers/Post";
import { Loader2 } from "lucide-react";

const UserPostsTab = ({ userId, className = "" }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  // state.publicUserPosts only exists when featureFlags.posts is enabled (see
  // redux/indexRedux.js). This tab is reachable any time a visitor opens the
  // "Activity" tab on a public profile, regardless of that flag, so this must
  // not destructure state.publicUserPosts directly or it crashes the tab.
  const { posts = [], loading, error, hasMore } = useSelector(
    (state) => state.publicUserPosts || {}
  );

  // Reset posts when user changes and fetch first page
  useEffect(() => {
    if (userId) {
      dispatch(resetPublicUserPosts());
      dispatch(fetchPublicUserPosts({ userId, skip: 0, limit: 10 }));
    }
  }, [userId, dispatch]);

  // Load more posts
  const loadMore = useCallback(() => {
    if (hasMore && !loading) {
      dispatch(fetchPublicUserPosts({ userId, skip: posts.length, limit: 10 }));
    }
  }, [dispatch, userId, posts.length, hasMore, loading]);

  if (loading && posts.length === 0) {
    return <div className="text-gray-400">{t('publicProfile.userPosts.loadingPosts')}</div>;
  }

  if (error) {
    return <div className="text-red-400">{t('publicProfile.userPosts.failedToLoad', { error })}</div>;
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {posts.map((post) => (
        <Post key={post.id} post={post} />
      ))}

      {hasMore && (
        <button
          onClick={loadMore}
          disabled={loading}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <Loader2 className="animate-spin w-5 h-5" /> {t('publicProfile.userPosts.loading')}
            </>
          ) : (
            t('publicProfile.userPosts.loadMore')
          )}
        </button>
      )}
    </div>
  );
};

export default UserPostsTab;
